'use client';

import _defineProperty from "/codebuild/output/src2470/src/s3/00/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "/codebuild/output/src2470/src/s3/00/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "/codebuild/output/src2470/src/s3/00/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useState, useRef, useEffect, createContext, useMemo, useContext, useCallback } from 'react';

function useLoadGsiScript() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var nonce = options.nonce,
      onScriptLoadSuccess = options.onScriptLoadSuccess,
      onScriptLoadError = options.onScriptLoadError;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      scriptLoadedSuccessfully = _useState2[0],
      setScriptLoadedSuccessfully = _useState2[1];

  var onScriptLoadSuccessRef = useRef(onScriptLoadSuccess);
  onScriptLoadSuccessRef.current = onScriptLoadSuccess;
  var onScriptLoadErrorRef = useRef(onScriptLoadError);
  onScriptLoadErrorRef.current = onScriptLoadError;
  useEffect(function () {
    var scriptTag = document.createElement('script');
    scriptTag.src = 'https://accounts.google.com/gsi/client';
    scriptTag.async = true;
    scriptTag.defer = true;
    scriptTag.nonce = nonce;

    scriptTag.onload = function () {
      var _a;

      setScriptLoadedSuccessfully(true);
      (_a = onScriptLoadSuccessRef.current) === null || _a === void 0 ? void 0 : _a.call(onScriptLoadSuccessRef);
    };

    scriptTag.onerror = function () {
      var _a;

      setScriptLoadedSuccessfully(false);
      (_a = onScriptLoadErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onScriptLoadErrorRef);
    };

    document.body.appendChild(scriptTag);
    return function () {
      document.body.removeChild(scriptTag);
    };
  }, [nonce]);
  return scriptLoadedSuccessfully;
}

var GoogleOAuthContext = createContext(null);

function GoogleOAuthProvider(_ref) {
  var clientId = _ref.clientId,
      nonce = _ref.nonce,
      onScriptLoadSuccess = _ref.onScriptLoadSuccess,
      onScriptLoadError = _ref.onScriptLoadError,
      children = _ref.children;
  var scriptLoadedSuccessfully = useLoadGsiScript({
    nonce: nonce,
    onScriptLoadSuccess: onScriptLoadSuccess,
    onScriptLoadError: onScriptLoadError
  });
  var contextValue = useMemo(function () {
    return {
      clientId: clientId,
      scriptLoadedSuccessfully: scriptLoadedSuccessfully
    };
  }, [clientId, scriptLoadedSuccessfully]);
  return React.createElement(GoogleOAuthContext.Provider, {
    value: contextValue
  }, children);
}

function useGoogleOAuth() {
  var context = useContext(GoogleOAuthContext);

  if (!context) {
    throw new Error('Google OAuth components must be used within GoogleOAuthProvider');
  }

  return context;
}

function extractClientId(credentialResponse) {
  var _a;

  var clientId = (_a = credentialResponse === null || credentialResponse === void 0 ? void 0 : credentialResponse.clientId) !== null && _a !== void 0 ? _a : credentialResponse === null || credentialResponse === void 0 ? void 0 : credentialResponse.client_id;
  return clientId;
}

var containerHeightMap = {
  large: 40,
  medium: 32,
  small: 20
};

function GoogleLogin(_ref2) {
  var onSuccess = _ref2.onSuccess,
      onError = _ref2.onError,
      useOneTap = _ref2.useOneTap,
      promptMomentNotification = _ref2.promptMomentNotification,
      _ref2$type = _ref2.type,
      type = _ref2$type === void 0 ? 'standard' : _ref2$type,
      _ref2$theme = _ref2.theme,
      theme = _ref2$theme === void 0 ? 'outline' : _ref2$theme,
      _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? 'large' : _ref2$size,
      text = _ref2.text,
      shape = _ref2.shape,
      logo_alignment = _ref2.logo_alignment,
      width = _ref2.width,
      locale = _ref2.locale,
      click_listener = _ref2.click_listener,
      containerProps = _ref2.containerProps,
      props = _objectWithoutProperties(_ref2, ["onSuccess", "onError", "useOneTap", "promptMomentNotification", "type", "theme", "size", "text", "shape", "logo_alignment", "width", "locale", "click_listener", "containerProps"]);

  var btnContainerRef = useRef(null);

  var _useGoogleOAuth = useGoogleOAuth(),
      clientId = _useGoogleOAuth.clientId,
      scriptLoadedSuccessfully = _useGoogleOAuth.scriptLoadedSuccessfully;

  var onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  var onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  var promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;
  useEffect(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;

    if (!scriptLoadedSuccessfully) return;
    (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.initialize(_objectSpread({
      client_id: clientId,
      callback: function callback(credentialResponse) {
        var _a;

        if (!(credentialResponse === null || credentialResponse === void 0 ? void 0 : credentialResponse.credential)) {
          return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef);
        }

        var credential = credentialResponse.credential,
            select_by = credentialResponse.select_by;
        onSuccessRef.current({
          credential: credential,
          clientId: extractClientId(credentialResponse),
          select_by: select_by
        });
      }
    }, props));
    (_f = (_e = (_d = window === null || window === void 0 ? void 0 : window.google) === null || _d === void 0 ? void 0 : _d.accounts) === null || _e === void 0 ? void 0 : _e.id) === null || _f === void 0 ? void 0 : _f.renderButton(btnContainerRef.current, {
      type: type,
      theme: theme,
      size: size,
      text: text,
      shape: shape,
      logo_alignment: logo_alignment,
      width: width,
      locale: locale,
      click_listener: click_listener
    });
    if (useOneTap) (_j = (_h = (_g = window === null || window === void 0 ? void 0 : window.google) === null || _g === void 0 ? void 0 : _g.accounts) === null || _h === void 0 ? void 0 : _h.id) === null || _j === void 0 ? void 0 : _j.prompt(promptMomentNotificationRef.current);
    return function () {
      var _a, _b, _c;

      if (useOneTap) (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.cancel();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, scriptLoadedSuccessfully, useOneTap, type, theme, size, text, shape, logo_alignment, width, locale]);
  return React.createElement("div", _objectSpread(_objectSpread({}, containerProps), {}, {
    ref: btnContainerRef,
    style: _objectSpread({
      height: containerHeightMap[size]
    }, containerProps === null || containerProps === void 0 ? void 0 : containerProps.style)
  }));
}

function googleLogout() {
  var _a, _b, _c;

  (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.disableAutoSelect();
}
/* eslint-disable import/export */


function useGoogleLogin(_ref3) {
  var _ref3$flow = _ref3.flow,
      flow = _ref3$flow === void 0 ? 'implicit' : _ref3$flow,
      _ref3$scope = _ref3.scope,
      scope = _ref3$scope === void 0 ? '' : _ref3$scope,
      onSuccess = _ref3.onSuccess,
      onError = _ref3.onError,
      onNonOAuthError = _ref3.onNonOAuthError,
      overrideScope = _ref3.overrideScope,
      state = _ref3.state,
      props = _objectWithoutProperties(_ref3, ["flow", "scope", "onSuccess", "onError", "onNonOAuthError", "overrideScope", "state"]);

  var _useGoogleOAuth2 = useGoogleOAuth(),
      clientId = _useGoogleOAuth2.clientId,
      scriptLoadedSuccessfully = _useGoogleOAuth2.scriptLoadedSuccessfully;

  var clientRef = useRef();
  var onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  var onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  var onNonOAuthErrorRef = useRef(onNonOAuthError);
  onNonOAuthErrorRef.current = onNonOAuthError;
  useEffect(function () {
    var _a, _b;

    if (!scriptLoadedSuccessfully) return;
    var clientMethod = flow === 'implicit' ? 'initTokenClient' : 'initCodeClient';
    var client = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.oauth2[clientMethod](_objectSpread({
      client_id: clientId,
      scope: overrideScope ? scope : "openid profile email ".concat(scope),
      callback: function callback(response) {
        var _a, _b;

        if (response.error) return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, response);
        (_b = onSuccessRef.current) === null || _b === void 0 ? void 0 : _b.call(onSuccessRef, response);
      },
      error_callback: function error_callback(nonOAuthError) {
        var _a;

        (_a = onNonOAuthErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onNonOAuthErrorRef, nonOAuthError);
      },
      state: state
    }, props));
    clientRef.current = client; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, scriptLoadedSuccessfully, flow, scope, state]);
  var loginImplicitFlow = useCallback(function (overrideConfig) {
    var _a;

    return (_a = clientRef.current) === null || _a === void 0 ? void 0 : _a.requestAccessToken(overrideConfig);
  }, []);
  var loginAuthCodeFlow = useCallback(function () {
    var _a;

    return (_a = clientRef.current) === null || _a === void 0 ? void 0 : _a.requestCode();
  }, []);
  return flow === 'implicit' ? loginImplicitFlow : loginAuthCodeFlow;
}

function useGoogleOneTapLogin(_ref4) {
  var onSuccess = _ref4.onSuccess,
      onError = _ref4.onError,
      promptMomentNotification = _ref4.promptMomentNotification,
      cancel_on_tap_outside = _ref4.cancel_on_tap_outside,
      prompt_parent_id = _ref4.prompt_parent_id,
      state_cookie_domain = _ref4.state_cookie_domain,
      hosted_domain = _ref4.hosted_domain,
      _ref4$use_fedcm_for_p = _ref4.use_fedcm_for_prompt,
      use_fedcm_for_prompt = _ref4$use_fedcm_for_p === void 0 ? false : _ref4$use_fedcm_for_p,
      disabled = _ref4.disabled,
      auto_select = _ref4.auto_select;

  var _useGoogleOAuth3 = useGoogleOAuth(),
      clientId = _useGoogleOAuth3.clientId,
      scriptLoadedSuccessfully = _useGoogleOAuth3.scriptLoadedSuccessfully;

  var onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  var onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  var promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;
  useEffect(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;

    if (!scriptLoadedSuccessfully) return;

    if (disabled) {
      (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.cancel();
      return;
    }

    (_f = (_e = (_d = window === null || window === void 0 ? void 0 : window.google) === null || _d === void 0 ? void 0 : _d.accounts) === null || _e === void 0 ? void 0 : _e.id) === null || _f === void 0 ? void 0 : _f.initialize({
      client_id: clientId,
      callback: function callback(credentialResponse) {
        var _a;

        if (!(credentialResponse === null || credentialResponse === void 0 ? void 0 : credentialResponse.credential)) {
          return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef);
        }

        var credential = credentialResponse.credential,
            select_by = credentialResponse.select_by;
        onSuccessRef.current({
          credential: credential,
          clientId: extractClientId(credentialResponse),
          select_by: select_by
        });
      },
      hosted_domain: hosted_domain,
      cancel_on_tap_outside: cancel_on_tap_outside,
      prompt_parent_id: prompt_parent_id,
      state_cookie_domain: state_cookie_domain,
      use_fedcm_for_prompt: use_fedcm_for_prompt,
      auto_select: auto_select
    });
    (_j = (_h = (_g = window === null || window === void 0 ? void 0 : window.google) === null || _g === void 0 ? void 0 : _g.accounts) === null || _h === void 0 ? void 0 : _h.id) === null || _j === void 0 ? void 0 : _j.prompt(promptMomentNotificationRef.current);
    return function () {
      var _a, _b, _c;

      (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.cancel();
    };
  }, [clientId, scriptLoadedSuccessfully, cancel_on_tap_outside, prompt_parent_id, state_cookie_domain, hosted_domain, use_fedcm_for_prompt, disabled, auto_select]);
}
/**
 * Checks if the user granted all the specified scope or scopes
 * @returns True if all the scopes are granted
 */


function hasGrantedAllScopesGoogle(tokenResponse, firstScope) {
  var _c2;

  var _a, _b, _c;

  if (!(window === null || window === void 0 ? void 0 : window.google)) return false;

  for (var _len = arguments.length, restScopes = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    restScopes[_key - 2] = arguments[_key];
  }

  return ((_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.oauth2) === null || _c === void 0 ? void 0 : (_c2 = _c).hasGrantedAllScopes.apply(_c2, [tokenResponse, firstScope].concat(restScopes))) || false;
}
/**
 * Checks if the user granted any of the specified scope or scopes.
 * @returns True if any of the scopes are granted
 */


function hasGrantedAnyScopeGoogle(tokenResponse, firstScope) {
  var _c3;

  var _a, _b, _c;

  if (!(window === null || window === void 0 ? void 0 : window.google)) return false;

  for (var _len2 = arguments.length, restScopes = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
    restScopes[_key2 - 2] = arguments[_key2];
  }

  return ((_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.oauth2) === null || _c === void 0 ? void 0 : (_c3 = _c).hasGrantedAnyScope.apply(_c3, [tokenResponse, firstScope].concat(restScopes))) || false;
}

export { GoogleLogin, GoogleOAuthProvider, googleLogout, hasGrantedAllScopesGoogle, hasGrantedAnyScopeGoogle, useGoogleLogin, useGoogleOAuth, useGoogleOneTapLogin };